import React, {useState, useEffect} from "react";
import { IProfile } from '../../../interface/IProfile.ts';
import { useProfileApi } from '../../../features/auth/hooks/use-profile-api.ts'
import { ApiResponseType } from "../../../features/api/types.ts";
import Form from "../../Form/Form.tsx";
import Button from '../../Form/Button.tsx';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../Cropper/canvasUtils';
import TextInput from "../../Form/TextInput.tsx";
import ApiCheck from '../../Structure/ApiCheck.tsx';
import { IOption } from "../../../interface/IOption.ts";
import { useListsApi } from "../../../features/auth/hooks/use-lists-api.ts";
import FileInput from "../../Form/FileInput.tsx";
import Checkboxes from "../../Form/Checkboxes.tsx";
import { useCertificatesApi } from "../../../features/auth/hooks/use-certifictates-api.ts";
import { ICertificate } from "../../../interface/ICertificate.ts";
import '../../../scss/components/Certificates.scss';
import { useHelper } from "../../../features/functions/helper.tsx";
import { useProfileContext } from "../../../features/auth/hooks/use-profile-context.ts";

const TabCommon = ({
    profile,
    setProfile
}:{
    profile:IProfile
    setProfile: Function
}) => {
    //Start General Form States and Functions
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [certsLoading, setCertsLoading] = useState<boolean>(true);
    const [certTypes, setCertTypes] = useState<Array<string>>([]);
    const [nextRecertification, setNextRecertification] = useState<string|boolean>(false);
    const [hasEmeritus, setHasEmeritus] = useState<boolean>(false);
    const [responseMessage, setResponseMessage] = useState<string>('');
    const [responseMessageClass, setResponseMessageClass] = useState<string>('');
    const { postProfile } = useProfileApi();
    const { getCertificates } = useCertificatesApi();
    const [errors, setErrors] = useState<any>({});
    const [unSavedChanges, setUnSavedChanges] = useState(false);
    const [hasInvalidForm, setHasInvalidForm] = useState(false);
    // ZP candidates (not yet validated) have no profile picture and no re-certification data
    const { needsInitialReview } = useProfileContext();

    const { formatDate, sortCertificateStringArray } = useHelper();

    const expectedAmountPublishAdresses:number = 2;

    const [formData, setFormData] = useState<any>({
        picture: profile.picture,
        publish_address: profile.publish_address ?? null,
    });

    const handleChange = (event, name = '', value = null) => {
        setFormData({ ...formData, [name ? name : event.target.name]: value });
        !unSavedChanges && setUnSavedChanges(true);
    };

    //Start Certs and Status State and Functions    
    const [cpdCredits] = useState(profile.cpd_credits ? Number(profile.cpd_credits).toLocaleString('de-DE') : '') // Anzahl CPD-Credits
    const [suspendedTill, setSuspendedTill] = useState<string|boolean>(false) // Suspendiert bis
    const [suspendedFrom, setSuspendedFrom] = useState<string|boolean>(false) // Suspendiert von
    const [pausedTill, setPausedTill] = useState<string|boolean>(false) // Ruhend bis
    const [pausedFrom, setPausedFrom] = useState<string|boolean>(false) // Ruhend ab

    const handleSubmit = (event) => {
        event.preventDefault();

        let postedData = {
            ...formData,
            picture: formData.picture
        };
        if (needsInitialReview) {
            delete postedData.picture;
            delete postedData.publish_address;
        } else {
            if (formData.publish_address.length <= 0) {
                setErrors({
                    'publish_address': 'Bitte wählen Sie eine der Optionen'
                });
                return;
            }

            let publishAddressIds = formData.publish_address!.map(el => {
                return el.value;
            });
            postedData.publish_address = publishAddressIds;
        }

        setUnSavedChanges(false);
        setIsLoading(true);

        setErrors({});
        postProfile(postedData).then(res => {
            setIsLoading(false);
            if (res.type === ApiResponseType.OK) {
                if (res.payload.success)  {
                    setResponseMessage('Profil erfolgreich gespeichert');
                    setResponseMessageClass('success');
                    setProfile({...profile, ...postedData});
                    setTimeout(() => {
                        setResponseMessage('');
                    }, 5000);
                    setHasInvalidForm(false);
                }
            } else {
                let errors = res.payload.errors!;
                if (!errors.general_error) {
                    setResponseMessage('Fehler. Bitte korrigieren Sie den Fehler und versuchen Sie es erneut.');
                    setResponseMessageClass('error');
                }
                setErrors(errors);
            }
        })
    }

    //End General Form States and Functions

    //Start Image State and Functions
    const [showImageUploadButton, setShowImageUploadButton] = useState(!profile.picture);
    const [showEditImageButton, setShowEditImageButton] = useState(profile.picture && profile.picture.length > 0);
    const [showDeleteImageButton, setShowDeleteImageButton] = useState(profile.picture && profile.picture.length > 0);
    const [showCancelButton, setShowCancelButton] = useState(false);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [fileUploadErrors, setFileUploadErrors] = useState<String>('');
    const [currentProfileImage, setCurrentProfileImage] = useState<String>('');

    const reader = new FileReader();

    const allowedFileTypes = ['image/png', 'image/jpeg', 'image/jpg'];

    reader.addEventListener('load', () => {
        let result = reader.result;
        if (result) {
            handleChange(null, 'picture', String(result).split(',')[1]);
        }
    }, false);

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels)
    }

    const showCroppedImage = async () => {
        try {
            const croppedImage = await getCroppedImg(
                formData.picture,
                croppedAreaPixels,
                rotation
            )
            handleChange(null, 'picture', String(croppedImage).split(',')[1]);
            !unSavedChanges && setUnSavedChanges(true);
            setShowSaveButton(false);
            setShowEditImageButton(true);
            setShowDeleteImageButton(true);
            setShowCancelButton(false);
            setCurrentProfileImage(String(croppedImage).split(',')[1]);
        } catch (e) {
            console.error(e)
        }
    }
    //End Image State and Functions

    //Start Adress State and Functions
    const [publishAddressList, setPublishAddressList] = useState<Array<any>>([]);
    const [publishAddressesLoading, setPublishAddressesLoading] = useState<boolean>(true);

    const [apiErrors, setApiErrors] = useState<any>();
    const { getList } = useListsApi();
    //End Adress State and Functions


    useEffect(() => {
        let formDataClone = structuredClone(formData);
        //Start Adress Effect State
        getList('publish_address').then(res => {
            let optionList:Array<IOption> = [];
            if (res) {
                res.forEach((item:any) => {
                    optionList.push({
                        value: item.id,
                        label: item.label.charAt(0).toUpperCase() + item.label.slice(1)
                    });
                });
                setPublishAddressList(optionList);
                let adressesFromOptions:Array<IOption> = [];
                adressesFromOptions = optionList.filter(singleAdressOption => {
                    return profile.publish_address?.includes(singleAdressOption.value);
                });
                formDataClone = { ...formDataClone, publish_address: adressesFromOptions }
                setFormData(formDataClone);
                setPublishAddressesLoading(false);
            }
        }).catch(() => {
            setApiErrors({
                errors: 'Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.'
            });
            setPublishAddressesLoading(false);
        });
        //End Adress Effect State

        //Start Certs and Status State     
        if (profile.suspended_till && profile.suspended_till !== '0') {
            let suspendedTillDate = new Date(Number(profile.suspended_till) * 1000);
            setSuspendedTill(formatDate(suspendedTillDate, 'dd.mm.yyyy'));
        } else {
            setSuspendedTill(false);
        }
        if (profile.suspended_from && profile.suspended_from !== '0') {
            let suspendedFromDate = new Date(Number(profile.suspended_from) * 1000);
            setSuspendedFrom(formatDate(suspendedFromDate, 'dd.mm.yyyy'));
        } else {
            setSuspendedFrom(false);
        }
        if (profile.paused_till && profile.paused_till !== '0') {
            let pausedTillDate = new Date(Number(profile.paused_till) * 1000);
            setPausedTill(formatDate(pausedTillDate, 'dd.mm.yyyy'));
        } else {
            setPausedTill(false);
        }
        if (profile.paused_from && profile.paused_from !== '0') {
            let pausedFromDate = new Date(Number(profile.paused_from) * 1000);
            setPausedFrom(formatDate(pausedFromDate, 'dd.mm.yyyy'));
        } else {
            setPausedFrom(false);
        }
        //End Certs and Status State

        //Start fetch Certificates for display only
        // getCertificates is rejected by the backend for unvalidated accounts (role_not_validated)
        if (needsInitialReview) {
            setCertsLoading(false);
        } else {
            getCertificates().then(data => {
                if (data.type === ApiResponseType.OK) {
                    let certificatesArray = data.payload;
                    if (certificatesArray && typeof certificatesArray == 'object' && certificatesArray.length > 0) {
                        let certTypes:Array<string> = [];
                        let foundNextRecertification: number|null = null;
                        certificatesArray.forEach((cert:ICertificate) => {
                            let type = cert.type.replaceAll(' ', '').toLowerCase();
                            if (type === 'fpsbemeritus') {
                                setHasEmeritus(true);
                            }
                            certTypes.push(type);
                            if (cert.next_recertification && parseInt(cert.next_recertification) > 0 && (foundNextRecertification === null || parseInt(cert.next_recertification) < foundNextRecertification)) {
                                foundNextRecertification = parseInt(cert.next_recertification);
                            }
                        });
                        if (foundNextRecertification) {
                            let foundNextRecertificationDate = new Date(foundNextRecertification * 1000);
                            setNextRecertification(formatDate(foundNextRecertificationDate, 'dd.mm.yyyy'));
                        }
                        certTypes = sortCertificateStringArray(certTypes);
                        setCertTypes(certTypes);
                    }
                } else {
                    setApiErrors(data.payload);
                }
                setCertsLoading(false);
            }).catch(() => {
                setApiErrors({
                    errors: 'Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.'
                });
                setCertsLoading(false);
            });
        }
        //End fetch Certificates for display only

        // Publication of contact data is only asked once a candidate is validated
        if (!needsInitialReview && (!profile.publish_address || profile.publish_address.length <= 0)) {
            setHasInvalidForm(true);
            setErrors({
                'publish_address': 'Bitte wählen Sie eine der Optionen'
            });
        }

    }, []);

    return (
        <div className="single-tab-item">
            <Form
                unSavedChanges={unSavedChanges}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                responseMessage={responseMessage}
                responseMessageClass={responseMessageClass}
                generalErrors={ errors.general_error ? errors.general_error : null}
                displayRequiredHint={!needsInitialReview}
                hasInvalidForm={hasInvalidForm}
                customSubmit={needsInitialReview}
            >
                <TextInput
                    onChange={() => {return false;}}
                    value={profile.real_number}
                    label='Ihre FPSB-Nummer'
                    readOnly={true}
                    disabled={true}
                    name='fpsb_number'
                />
                { needsInitialReview ? null : publishAddressesLoading ? (
                    <div className="form-group checkboxes label-top">
                        <label key={0}>Welche Ihrer Kontaktdaten sollen auf der FPSB-Webseite veröffentlicht werden?</label>
                        <div className="checkbox-inputs-wrapper">
                            {[...Array(expectedAmountPublishAdresses)].map((k,i) =>
                                <div className="single-checkbox" key={'cl_' + i}>
                                    <input type="checkbox" disabled />
                                    <label><div className="checkbox-loader"></div></label>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <ApiCheck errors={apiErrors ? apiErrors : null}>
                        <Checkboxes
                            wrapperClassName='label-top'
                            label='Welche Ihrer Kontaktdaten sollen auf der FPSB-Webseite veröffentlicht werden? *'
                            name='publish_address'
                            onChange={(item:IOption, wasChecked:boolean) => {
                                if (wasChecked) {
                                    let x = formData.publish_address.filter(el => {
                                        return el.value !== item.value
                                    });
                                    handleChange(null, 'publish_address', x);
                                } else {
                                    let x = structuredClone(formData.publish_address);
                                    x.push(item);
                                    handleChange(null, 'publish_address', x);
                                }
                            }}
                            value={formData?.publish_address}
                            options={publishAddressList}
                            error={errors['publish_address']}
                            description='Bitte wählen Sie mindestens eine Option aus – es lassen sich auch beide Kontaktdatensätze veröffentlichen.'
                        />
                    </ApiCheck>
                ) }
                { !needsInitialReview && (
                <div className="form-group profile-image">
                    <label>Profilbild<br/><br/><em>Dieses Profilbild wird in Ihrem öffentlichen Profil angezeigt</em></label>
                    <div className="profile-image-container">
                        {formData.picture && (
                            <div className="image-container">
                                { showSaveButton ? (
                                    <Cropper
                                        image={`data:image/jpeg;base64,${formData.picture}`}
                                        crop={crop}
                                        rotation={rotation}
                                        zoom={zoom}
                                        aspect={1 / 1}
                                        onCropChange={setCrop}
                                        onRotationChange={setRotation}
                                        onCropComplete={onCropComplete}
                                        onZoomChange={setZoom}
                                    />
                                ) : (
                                    <img
                                        alt="Profilbild"
                                        src={`data:image/jpeg;base64,${formData.picture}`}
                                        className={showImageUploadButton ? 'uploading' : ''}
                                    />
                                )}
                            </div>
                        )}
                        <div className='buttons'>
                            {showImageUploadButton && (
                                <FileInput
                                    label="Datei wählen"
                                    accept={allowedFileTypes.join(', ')}
                                    name="myImage"
                                    onChange={(event) => {
                                        const [file] = event.target.files;
                                        if (allowedFileTypes.includes(file.type)) {
                                            if (file) {
                                                reader.readAsDataURL(file);
                                            }
                                            setShowImageUploadButton(false);
                                            setShowSaveButton(true);
                                        } else {
                                            setFileUploadErrors('Dateiart nicht erlaubt');
                                        }
                                    }}
                                />
                            )}
                            { showEditImageButton && (
                                <Button
                                    buttonType='outline'
                                    onClick={(e) => {
                                        setShowImageUploadButton(!showImageUploadButton)
                                        setShowEditImageButton(false);
                                        setShowDeleteImageButton(false);
                                        setShowCancelButton(true);
                                        setCurrentProfileImage(formData.picture);
                                    }}
                                    icon={'edit'}
                                >Bild ändern</Button>
                            )}
                            { showDeleteImageButton && (
                                <Button
                                    buttonType='outline button-danger'
                                    onClick={() => {
                                        handleChange(null, 'picture', null);
                                        setShowDeleteImageButton(false);
                                        setShowImageUploadButton(!showImageUploadButton)
                                        setShowEditImageButton(false);
                                        setShowCancelButton(false);
                                    }}
                                    icon={'x'}
                                >Bild entfernen</Button>
                            )}
                            { showSaveButton && (
                                <Button
                                    buttonType='outline'
                                    onClick={() => {
                                        showCroppedImage();
                                    }}
                                    icon={'check'}
                                >Ausschnitt speichern</Button>
                            ) }
                            {showCancelButton && (
                                <Button
                                    buttonType='outline'
                                    onClick={(e) => {
                                        setShowDeleteImageButton(true);
                                        setShowEditImageButton(true);
                                        setShowEditImageButton(true);
                                        setShowImageUploadButton(false);
                                        setShowCancelButton(false);
                                        setShowSaveButton(false);
                                        handleChange(null, 'picture', currentProfileImage);
                                    }}
                                    icon={'x'}
                                >Abbrechen</Button>
                            )}
                            { fileUploadErrors && (
                                <span className="message error">{fileUploadErrors}</span>
                            )}
                        </div>
                    </div>
                </div>
                ) }
                { (!needsInitialReview || suspendedTill || suspendedFrom || pausedTill || pausedFrom) && (
                    <h4>Zertifizierung und Status</h4>
                ) }
                { !needsInitialReview && (
                <div className="form-group">
                    <label>Ihre Zertifikate</label>
                    { certsLoading ? (
                        <div className="cert-image-loader"></div>
                    ) : (
                        hasEmeritus || certTypes.length > 0 ? (
                            hasEmeritus ? (
                                <img className="emeritus" src={require("../../../img/files/certificates/fpsbemeritus.jpg")} alt="Zertifikat FPSB Emeritus"/>
                            ) : (
                                <div className="certificate-list">
                                    { certTypes.map((certificate:string, key:number) => (
                                        <img src={require(`../../../img/files/certificates/${certificate}.jpg`)} key={'img_'+key} alt={`Zertifikat ${certificate}`}/>
                                    ))}
                                </div>
                            )
                        ) : <p>Noch keine Zertifikate vorhanden</p>
                    ) }
                </div>
                ) }
                { !needsInitialReview && (
                    <>
                        <TextInput
                            value={cpdCredits}
                            label='Anzahl notwendige CPD-Credits für Re-Zertifizierung'
                            required={false}
                            name='cpdCredits'
                            disabled={true}
                        />
                        <TextInput
                            value={nextRecertification}
                            label='Nächste Re-Zertifizierung'
                            required={false}
                            name='nextRecertification'
                            disabled={true}
                        />
                    </>
                ) }

                { suspendedTill && (
                    <TextInput
                        value={String(suspendedTill)}
                        label='Suspendiert bis'
                        required={false}
                        name='suspendedTill'
                        disabled={true}
                        className='date'
                    />
                ) }
                { suspendedFrom && (
                    <TextInput
                        value={String(suspendedFrom)}
                        label='Suspendiert von'
                        required={false}
                        name='suspendedFrom'
                        disabled={true}
                        className='date'
                    />
                ) }
                { pausedTill && (
                    <TextInput
                        value={String(pausedTill)}
                        label='Ruhend bis'
                        required={false}
                        name='pausedTill'
                        disabled={true}
                        className='date'
                    />
                ) }
                { pausedFrom && (
                    <TextInput
                        value={String(pausedFrom)}
                        label='Ruhend ab'
                        required={false}
                        name='pausedFrom'
                        disabled={true}
                        className='date'
                    />
                ) }
            </Form>
        </div>
    )
}

export default TabCommon;